'use client'

import { createContext, useContext } from 'react'

type NonceContextType = string | undefined

const NonceContext = createContext<NonceContextType>(undefined)

export function NonceProvider({
  nonce,
  children,
}: {
  nonce: string | undefined
  children: React.ReactNode
}) {
  return <NonceContext.Provider value={nonce}>{children}</NonceContext.Provider>
}

/**
 * Hook to access the CSP nonce for the current request.
 *
 * A CSP nonce (Content Security Policy "number used once") is a unique,
 * cryptographically strong random value generated by a web server for each request.
 * It is used to allow specific inline scripts and styles to run on a page while
 * blocking all others, as a defense against Cross-Site Scripting (XSS) attacks.
 *
 * The nonce should be added to any inline `<style>` or `<script>` tags to make them
 * CSP-compliant when `'unsafe-inline'` is removed from the Content Security Policy.
 *
 * @returns The CSP nonce string for the current request, or `undefined` if not available
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const cspNonce = useCspNonce()
 *
 *   return (
 *     <style nonce={cspNonce}>
 *       {`.my-class { color: red; }`}
 *     </style>
 *   )
 * }
 * ```
 *
 * @example
 * ```typescript
 * function DynamicScript() {
 *   const cspNonce = useCspNonce()
 *
 *   return (
 *     <script nonce={cspNonce}>
 *       {`console.log('This script is CSP-compliant')`}
 *     </script>
 *   )
 * }
 * ```
 *
 * @see https://nextjs.org/docs/15/app/guides/content-security-policy#nonces
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/nonce
 */
export function useCspNonce(): string | undefined {
  return useContext(NonceContext)
}
